import { GdsFilter } from 'tableTypes'
import { MappedDriver, Tumor } from 'reportViewer'
import { Evidence, EvidenceFilterConditions, Relation } from 'evidenceTypes'

export const filterByGds = <T extends { level: number }>(
    list: T[],
    gdsFilter: GdsFilter
): T[] => {
    if (!gdsFilter) return list
    return gdsFilter === 'negative'
        ? list.filter((item) => item.level < 0)
        : list.filter((item) => item.level >= 0)
}

export const filterByCategory = <T extends { category: string }>(
    list: T[],
    categories: string[]
): T[] => {
    if (!categories.length) return list
    return list.filter((item) => categories.includes(item.category))
}

/**
 * Returns true if the patient’s tumor/histology and the evidence’s
 * tumor/histology should be considered a “match” according to:
 *  - only explicit mismatches (both sides specified & different) block
 *  - any “unspecified” or “unknown” value on either side is a wildcard
 */
export function matchPatientEvidence(
    patient: Tumor,
    evidence: { tumor: string; histology: string }
): boolean {
    const normalize = (v: string): string | null => {
        if (!v) return null
        const s = v.trim().toLowerCase()
        // treat these as “no restriction”
        if (s === '' || s === 'unknown' || s === 'unspecified') return null
        return s
    }

    const ptTumor = normalize(patient.primaryTumorSite)
    const ptHist = normalize(patient.histologyType)
    const evTumor = normalize(evidence.tumor)
    const evHist = normalize(evidence.histology)

    // if evidence relation is unspecified-unspecified, reject it
    if (evTumor === null && evHist === null) {
        return false
    }

    // if both sides have a specific tumor and they differ → no match
    if (ptTumor && evTumor && ptTumor !== evTumor) return false

    // if both sides have a specific histology and they differ → no match
    return !(ptHist && evHist && ptHist !== evHist)
}

export const toggleCheckboxListFilter = <T extends string>(
    currentList: T[],
    item: T
): T[] => {
    return currentList.includes(item)
        ? currentList.filter((i) => i !== item)
        : [...currentList, item]
}

export function getDriverMatchKeys(driver: MappedDriver): string[] {
    const gene = driver.item?.gene?.name?.trim()
    const mutation = driver.item?.mutation?.name?.trim()
    const testType = driver.item?.testType?.trim()
    const testResult = driver.testResult?.trim()

    if (mutation && gene) {
        return [`${gene}-${mutation}`]
    }

    if (gene && testType && testResult) {
        return [`${gene}-${testType}-${testResult}`]
    }

    if (!gene && testType && testResult) {
        // No gene, but alteration type (e.g., MSI-H, TMB-H)
        return [`${testType}-${testResult}`]
    }

    return []
}

export function getRelationMatchKeys(relation: Relation): string[] {
    const { biomarker } = relation

    const gene = biomarker?.geneName?.trim()
    const mutation = biomarker?.mutationName?.trim()
    const testType = biomarker?.testType?.trim()
    const testResult = biomarker?.testResult?.trim()

    if (mutation && gene) {
        return [`${gene}-${mutation}`]
    }

    if (gene && testType && testResult) {
        return [`${gene}-${testType}-${testResult}`]
    }

    if (!gene && testType && testResult) {
        return [`${testType}-${testResult}`]
    }

    return []
}

export function filterEvidences(
    evidences: Evidence[],
    conditions: EvidenceFilterConditions,
    patientTumor?: Tumor,
    molecularProfileMatches?: Set<string>
): Evidence[] {
    return evidences.filter((evidence) => {
        return evidence.relations.some((relation) => {
            const matchesConditions = Object.entries(conditions).every(
                ([key, value]) => {
                    const relationValue = relation[key as keyof Relation]
                    return relationValue === value
                }
            )

            const matchesPatient = (() => {
                if (!patientTumor) return true
                return matchPatientEvidence(patientTumor, {
                    tumor: relation.tumor ?? '',
                    histology: relation.histology ?? ''
                })
            })()

            const matchesMolecularProfile = (() => {
                if (!molecularProfileMatches) return true

                const relationKeys = getRelationMatchKeys(relation)
                return relationKeys.some((key) =>
                    molecularProfileMatches.has(key)
                )
            })()

            return (
                matchesConditions && matchesPatient && matchesMolecularProfile
            )
        })
    })
}
